import type {StateCreator} from 'zustand'
import type {SafeHTMLString} from '@github-ui/safe-html'
import type {DiffEntry, SuggestionIdentifier, SuggestedChangeLineRange} from '@github-ui/diff-lines/conversation-types'
import {
  MAX_SUGGESTED_CHANGES_BATCH_SIZE,
  SUGGESTED_CHANGES_ERRORS,
  validateSuggestedChangeCanBeAddedToBatch,
  type SuggestionForBatchValidation,
} from '@github-ui/diff-lines/helpers/suggested-changes'
import type {CommentSnapshot} from '../../helpers/comment-snapshot'

/**
 * Single entry in the unified batch panel.
 *
 * Extends {@link SuggestionIdentifier} via the union members so suggestion-aware code
 * can interop with it directly. Membership is keyed on the composite
 * `(commentDatabaseId, suggestionIndex)` — matching the legacy slice — so a human
 * comment with multiple fenced suggestion blocks can have each block added
 * independently.
 */
type UnifiedBatchItemBase = {
  commentGlobalId: string
  commentDatabaseId: number
  path: string
  /**
   * Optional multi-range projection used by CCR suggestions that span multiple hunks
   * (built at the call site via `getLineRangesFromDiffEntries`). When present,
   * overlap detection uses this instead of the single `path`/`lineRange` pair.
   */
  pathLineRanges?: Array<{path: string; lineRange: SuggestedChangeLineRange}>
  isOutdated: boolean
  threadId?: number | null
  /**
   * Snapshot of the source comment captured at add-to-batch time (`author`,
   * `createdAt`, `bodyHTML`, `bodyTruncated`) so the Manage batch panel can
   * render the comment in-place. Built by `buildCommentSnapshot`; does not
   * re-resolve if the source comment is later edited or deleted.
   */
} & CommentSnapshot

/**
 * Discriminated union on `kind` so the compiler keeps the
 * `hasSuggestion` / `suggestionIndex` / `lineRange` combination honest per kind:
 *
 * - `ccr-with-suggestion`: automated review suggestion with a fenced suggestion
 *   block. CCR's legacy contract is one suggestion per comment, hence
 *   `suggestionIndex` is always `undefined`. `lineRange` is the primary hunk and
 *   `pathLineRanges` carries the multi-hunk projection when present.
 * - `ccr-without-suggestion`: automated review finding with no suggestion block
 *   (commentary / observation only). Doesn't participate in overlap validation.
 * - `human-with-suggestion`: human comment with at least one fenced ```suggestion
 *   block; `suggestionIndex` identifies which block within the comment. These
 *   are store-facing items and must have concrete suggestion data.
 * - `human-without-suggestion`: human comment with no suggestion block at all;
 *   doesn't participate in range/overlap validation.
 */
export type UnifiedBatchItem =
  | (UnifiedBatchItemBase & {
      kind: 'ccr-with-suggestion'
      hasSuggestion: true
      suggestionIndex: undefined
      lineRange: SuggestedChangeLineRange | undefined
      /**
       * One entry per file the suggestion touches, passed through from
       * `Comment.automatedComment.suggestion.diffEntries`. Rendered in the panel
       * via `AutomatedReviewDiffEntry` (the same component CCR uses in the thread).
       */
      suggestionFiles: DiffEntry[]
    })
  | (UnifiedBatchItemBase & {
      kind: 'ccr-without-suggestion'
      hasSuggestion: false
      suggestionIndex: undefined
      lineRange: SuggestedChangeLineRange | undefined
    })
  | (UnifiedBatchItemBase & {
      kind: 'human-with-suggestion'
      hasSuggestion: true
      suggestionIndex: number
      lineRange: SuggestedChangeLineRange
      /**
       * The `div.js-suggested-changes-blob` HTML extracted from `bodyHTML` at
       * this row's `suggestionIndex` (the same selector the thread parser uses).
       * Rendered in the panel's Suggestions section via `VerifiedHTMLDiv`.
       */
      suggestionBlobHTML: SafeHTMLString
    })
  | (UnifiedBatchItemBase & {
      kind: 'human-without-suggestion'
      hasSuggestion: false
      suggestionIndex: undefined
      lineRange: undefined
    })

/**
 * Thread-level hint used before a specific human suggestion block is selected.
 * This is only for UI decisions like hiding the row-level batch toggle; it is
 * intentionally not accepted by `addItem`.
 */
export type UnifiedBatchItemThreadHint = UnifiedBatchItemBase & {
  kind: 'human-with-suggestion'
  hasSuggestion: true
  suggestionIndex: undefined
  lineRange: undefined
}

export type UnifiedBatchItemKind = UnifiedBatchItem['kind']

/**
 * Committable = carries a suggestion block (`*-with-suggestion`) and is not
 * outdated. Comment-only items belong to the Fix with Copilot tab; outdated
 * suggestions can no longer be applied at the current head.
 *
 * Single source of truth for "can this item be committed": `getBatchComposition`
 * reads it to gate the Commit CTA and the apply-suggestions mutation reads it to
 * choose which comments to send, so the CTA's enabled state and the committed
 * payload can never drift apart.
 */
export function isCommittableSuggestion(item: UnifiedBatchItem): boolean {
  return item.hasSuggestion && !item.isOutdated
}

/** Composite key used for membership/dedup checks. */
const sameItem = (
  a: Pick<SuggestionIdentifier, 'commentDatabaseId' | 'suggestionIndex'>,
  b: Pick<SuggestionIdentifier, 'commentDatabaseId' | 'suggestionIndex'>,
): boolean => a.commentDatabaseId === b.commentDatabaseId && (a.suggestionIndex ?? null) === (b.suggestionIndex ?? null)

export interface UnifiedBatchSlice {
  unifiedBatch: {
    items: UnifiedBatchItem[]
    isPending: boolean
    /**
     * `true` while a hand-off mutation (Fix with Copilot / Commit suggestions) is
     * in flight.
     */
    isSubmitting: boolean
  }
  unifiedBatchActions: {
    addItem: (item: UnifiedBatchItem) => {ok: true} | {ok: false; reason: string}
    removeItem: (id: SuggestionIdentifier) => void
    clearBatch: () => void
    /**
     * Locks/unlocks the batch while a submission mutation is in flight.
     */
    setSubmitting: (submitting: boolean) => void
    markOutdated: (id: SuggestionIdentifier) => void
    /**
     * Marks every batched item as outdated. Called by the sibling
     * `ComparisonOutdatedSlice`'s `markComparisonOutdated` action — there is no
     * direct UI caller today.
     */
    markAllOutdated: () => void
    loadFromStorage: (items: UnifiedBatchItem[]) => void
  }
}

export type UnifiedBatchSliceOpts = {
  initialItems?: UnifiedBatchItem[]
  /**
   * When `false`, every mutator is a no-op and `addItem` returns
   * `{ok: false, reason: 'unified batch is disabled'}`. The state shape is preserved
   * so selectors keep returning safe empty values regardless of the flag.
   */
  enabled?: boolean
}

const DISABLED_REASON = 'unified batch is disabled'
const SUBMITTING_REASON = "can't add to the batch while it's being submitted."

const toValidationShape = (item: UnifiedBatchItem): SuggestionForBatchValidation => ({
  threadId: item.threadId ?? null,
  pathLineRanges:
    item.pathLineRanges && item.pathLineRanges.length > 0
      ? item.pathLineRanges
      : item.lineRange
        ? [{path: item.path, lineRange: item.lineRange}]
        : [],
})

export const createUnifiedBatchSlice =
  (
    opts: UnifiedBatchSliceOpts = {},
  ): StateCreator<UnifiedBatchSlice, [['zustand/devtools', never]], [], UnifiedBatchSlice> =>
  (set, get) => {
    const enabled = opts.enabled ?? true
    const seedItems = enabled ? (opts.initialItems ?? []) : []
    return {
      unifiedBatch: {
        items: seedItems,
        isPending: seedItems.length > 0,
        isSubmitting: false,
      },
      unifiedBatchActions: {
        addItem: (item: UnifiedBatchItem) => {
          if (!enabled) return {ok: false, reason: DISABLED_REASON}
          if (get().unifiedBatch.isSubmitting) return {ok: false, reason: SUBMITTING_REASON}
          const current = get().unifiedBatch.items
          const alreadyExists = current.some(existing => sameItem(existing, item))
          if (alreadyExists) {
            return {ok: true}
          }

          if (current.length >= MAX_SUGGESTED_CHANGES_BATCH_SIZE) {
            return {ok: false, reason: SUGGESTED_CHANGES_ERRORS.BATCH_TOO_BIG}
          }

          if (item.hasSuggestion) {
            // Pre-check above covers non-suggestion items; here we just validate suggestion-bearing
            // overlap among the suggestion-bearing subset.
            const pending = current.filter(existing => existing.hasSuggestion).map(toValidationShape)
            const validation = validateSuggestedChangeCanBeAddedToBatch(toValidationShape(item), pending)
            if (!validation.isValid) {
              return {ok: false, reason: validation.reason ?? SUGGESTED_CHANGES_ERRORS.INVALID_RANGE}
            }
          }

          set(
            state => {
              const items = [...state.unifiedBatch.items, item]
              return {unifiedBatch: {...state.unifiedBatch, items, isPending: items.length > 0}}
            },
            undefined,
            'unifiedBatch#addItem',
          )

          return {ok: true}
        },
        removeItem: (id: SuggestionIdentifier) => {
          if (!enabled) return
          set(
            state => {
              const items = state.unifiedBatch.items.filter(item => !sameItem(item, id))
              return {unifiedBatch: {...state.unifiedBatch, items, isPending: items.length > 0}}
            },
            undefined,
            'unifiedBatch#removeItem',
          )
        },
        clearBatch: () => {
          if (!enabled) return
          set(
            state => ({unifiedBatch: {...state.unifiedBatch, items: [], isPending: false}}),
            undefined,
            'unifiedBatch#clearBatch',
          )
        },
        setSubmitting: (submitting: boolean) => {
          if (!enabled) return
          set(
            state => ({unifiedBatch: {...state.unifiedBatch, isSubmitting: submitting}}),
            undefined,
            'unifiedBatch#setSubmitting',
          )
        },
        markOutdated: (id: SuggestionIdentifier) => {
          if (!enabled) return
          set(
            state => {
              const target = state.unifiedBatch.items.find(item => sameItem(item, id))
              if (!target || target.isOutdated) return state
              const items = state.unifiedBatch.items.map(item =>
                sameItem(item, id) ? {...item, isOutdated: true} : item,
              )
              return {unifiedBatch: {...state.unifiedBatch, items}}
            },
            undefined,
            'unifiedBatch#markOutdated',
          )
        },
        markAllOutdated: () => {
          if (!enabled) return
          set(
            state => {
              if (state.unifiedBatch.items.length === 0) return state
              if (state.unifiedBatch.items.every(item => item.isOutdated)) return state
              const items = state.unifiedBatch.items.map(item => (item.isOutdated ? item : {...item, isOutdated: true}))
              return {unifiedBatch: {...state.unifiedBatch, items}}
            },
            undefined,
            'unifiedBatch#markAllOutdated',
          )
        },
        loadFromStorage: (items: UnifiedBatchItem[]) => {
          if (!enabled) return
          set(
            state => ({unifiedBatch: {...state.unifiedBatch, items, isPending: items.length > 0}}),
            undefined,
            'unifiedBatch#loadFromStorage',
          )
        },
      },
    }
  }
